import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaCalendarDay, FaChartBar, FaRegClock } from 'react-icons/fa';
import { FaCalendarDays } from 'react-icons/fa6';
import { TbMathAvg } from 'react-icons/tb';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend, ChartEvent, ActiveElement, Chart,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import * as Motion from 'motion/react';
import { DateRange, DayPicker } from '@daypicker/react';
import '@daypicker/react/style.css';
import Loader from '../../components/Loaders';
import { UserAvatar } from '../../components/UserProfileItem';
import { getUser } from '../../api/v1/users';
import { getAllStatistics } from '../../api/v1/sessions';
import { gameFilterOptions, statTypeOptions } from './options';
import { animationStaggeredItem } from '../../animations';
import { formatChartData } from './charts';
import { ActivityId } from '../../constants';
import { formatDate, prettyPrintMsTime } from '../../util';
import '../../styles/screen.scss';
import './styles.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { motion } = Motion;


const UserStatistics = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const statTypeQuery = searchParams.get('statType');
  const activityIdQuery = searchParams.get('activityId');
  const dateQuery = searchParams.get('date');

  const [activitySelectActive, setActivitySelectActive] = useState(false);
  const [user, setUser] = useState<userType>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [activityId, setActivityId] = useState(activityIdQuery || '');
  const [statType, setStatType] = useState(statTypeQuery || 'dailyMaxScore');
  const [dateRange, setDateRange] = useState<Date | undefined>(dateQuery ? new Date(dateQuery.replace(/-/g, '\/')) : undefined);
  const [selectedCalendarMonth, setSelectedCalendarMonth] = useState<Date>(new Date());
  const [statistics, setStatistics] = useState<statisticsType>({ labels: [], datasets: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [userTotalPlayTime, setUserTotalPlayTime] = useState(0);
  const [userBestDayDate, setUserBestDayDate] = useState<Date | null>();
  const [userBestDayScore, setUserBestDayScore] = useState(0);
  const [userAvgScore, setUserAvgScore] = useState(0);
  const [userVarScore, setUserVarScore] = useState(0);
  const [userDaysActive, setUserDaysActive] = useState(0);
  const navigate = useNavigate();
  const { userId } = useParams();

  const loadUser = async (userId: string) => {
    const result: any = await getUser(userId);

    setUser(result?.data);
  };

  const loadActivityStatistics = async (query: string) => {
    const result: any = await getAllStatistics(query);

    setStatistics(formatChartData(statType, result?.data));
  };

  const loadUserMetrics = async (userId: string) => {
    const totalTimeResult: any = await getAllStatistics(`filter[userId]=${userId}&output=totalTime&output=limit=1`);
    setUserTotalPlayTime(totalTimeResult?.data[0]?.totalTime);

    const dailyAverageResult: any = await getAllStatistics(`filter[userId]=${userId}&output=avgScore`);
    setUserAvgScore(dailyAverageResult?.data[0]?.avgScore);
    setUserVarScore(dailyAverageResult?.data[0]?.varScore);

    const userDaysActiveResult: any = await getAllStatistics(`filter[userId]=${userId}&group=daily`);
    setUserDaysActive(userDaysActiveResult?.data?.length);

    const bestDayResult: any = await getAllStatistics(`filter[userId]=${userId}&group=daily&output=maxScore`);
    setUserBestDayDate(bestDayResult?.data[0]?.daysgroup);
    setUserBestDayScore(bestDayResult?.data[0]?.maxScore);
  };

  useEffect(() => {
    (async () => {
      if (!statType) return;

      const queryFormattedDate = dateRange?.toLocaleDateString('en-CA', { timeZone: 'UTC' }) || '';
      const dateQuery = queryFormattedDate ? `&filter[date]=${queryFormattedDate}` : '';

      let query;

      setSearchParams({
        activityId: activityId,
        statType: statType,
        ...(queryFormattedDate && { date: queryFormattedDate })
      }, {
        replace: true
      });

      switch (statType) {
        case 'dailyMaxScore':
          query = `group=daily&output=maxScore&filter[userId]=${userId}&filter[gameId]=${activityId}${dateQuery}`;
          break;

        case 'dailyPlayTime':
          query = `group=daily&output=totalTime&filter[userId]=${userId}&filter[gameId]=${activityId}${dateQuery}`;
          break;

        case 'favoriteGame':
          query = `filter[userId]=${userId}&group=byGameId&output=totalTime&output=MaxScore,limit=1${dateQuery}`;
          break;

        default:
          return;
      }

      await loadActivityStatistics(query);
    })();
  }, [userId, statType, activityId, dateRange]);

  useEffect(() => {
    if (statType) {
      if (statTypeOptions[statType]?.activitySelectActive) {
        setActivitySelectActive(true);
      } else {
        setActivityId('');
        setActivitySelectActive(false);
      }
    }

  }, [statType]);

  useEffect(() => {
    (async () => {
      if (userId) {
        await loadUser(userId);
        await loadUserMetrics(userId);
      }
      setIsLoading(false);
    })();
  }, [userId]);

  useEffect(() => {
    if (datePickerOpen && dateRange) {
      setSelectedCalendarMonth(dateRange);
    }
  }, [datePickerOpen, dateRange]);

  return (
    <>
      <Motion.AnimatePresence>
        {isLoading && <Loader/>}
      </Motion.AnimatePresence>

      <div className="screen-navigation">
        <button className="screen-back-button" onClick={() => navigate(-1)}>&larr; Go Back</button>
      </div>

      <motion.div variants={animationStaggeredItem} className="screen-header">
        <div className="screen-title-wrapper">
          <UserAvatar userName={user?.userName}/>

          <div>
            <h2 className="screen-title">
              {user?.userName}'s Statistics
            </h2>

            <p className="screen-subtitle">
              <b>User ID:</b> {user?.id}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-section flex-3 scroll-overflow">
        <div className="section-heading-wrapper">
          <h3 className="section-heading">
            <FaChartBar/>
            Statistics
          </h3>

          <div className="chart-controls-wrapper">
            <div className="chart-filter-wrapper">
              <label>Stat Type</label>
              <select value={statType} onChange={(event) => setStatType(event.target.value)}
                      name="activity-selection">
                <option disabled value={''}>Select a stat type...</option>
                {
                  Object.keys(statTypeOptions).map(statType => (
                    <option key={statType} value={statType}>{statTypeOptions[statType].name('')}</option>
                  ))
                }
              </select>
            </div>

            <div className={`chart-filter-wrapper ${!activitySelectActive && 'disabled'}`}>
              <label>Activity</label>
              <select disabled={!activitySelectActive} value={activityId}
                      onChange={(event) => setActivityId(event.target.value)}
                      name="activity-selection">
                <option value={''}>All Activities</option>
                {
                  Object.keys(gameFilterOptions).map(gameId => (
                    <option key={gameId} value={gameId}>{gameFilterOptions[gameId]}</option>
                  ))
                }
              </select>
            </div>

            <div className={`chart-filter-wrapper date-picker-wrapper`}>
              <label>Date</label>
              <button
                className="date-picker-button"
                onClick={() => setDatePickerOpen(!datePickerOpen)}
              >
                {
                  dateRange ?
                    dateRange?.toLocaleDateString() :
                    "Select date"
                }
              </button>

              {datePickerOpen && (
                <div className="date-picker-popup">
                  <DayPicker
                    animate
                    mode="single"
                    month={selectedCalendarMonth}
                    selected={dateRange}
                    onSelect={setDateRange}
                    onMonthChange={setSelectedCalendarMonth}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="chart-container">
          {
            !statType ?
              <p className="text-vertically-align text-center text-secondary">Select Stat Type</p> :

              <Bar
                data={statistics}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      labels: {
                        color: '#dfdfdf'
                      },
                      position: 'bottom'
                    },
                    title: {
                      color: '#efefef',
                      display: true,
                      text: statTypeOptions[statType]?.name(activityId as ActivityId),
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => statTypeOptions[statType]?.tooltipLabel(context)
                      },
                    },
                  },
                  onClick(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
                    // if (!elements.length) {
                    //   return;
                    // }
                    //
                    // const element = elements[0];
                    //
                    // // Index of the clicked bar
                    // const index = element.index;
                    //
                    // // X-axis value
                    // const xValue = statistics?.labels[index];
                    //
                    // console.log(xValue);
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        color: '#efefef',
                        display: true,
                        text: statTypeOptions[statType]?.yAxisLabel,
                      },
                      ticks: {
                        color: '#dfdfdf'
                      }
                    },
                    x: {
                      title: {
                        color: '#efefef',
                        display: true,
                        text: statTypeOptions[statType]?.xAxisLabel,
                      },
                      ticks: {
                        color: '#dfdfdf'
                      }
                    },
                  },
                }}
              />
          }
        </div>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-row flex-3">
        <div className="screen-stat-pane">
          <div className="stat-pane-icon-wrapper">
            <h3 className="section-pane-icon">
              <FaRegClock/>
            </h3>
          </div>

          <div className="stat-pane-stats">
            <p className="stat-pane-title text-secondary">
              Total Play Time
            </p>

            <h3 className="stat-pane-main-stat">
              {userTotalPlayTime}s
            </h3>

            <p className="stat-pane-title text-secondary">
              {prettyPrintMsTime(userTotalPlayTime * 1000)}
            </p>
          </div>
        </div>

        <div className="screen-stat-pane">
          <div className="stat-pane-icon-wrapper">
            <h3 className="section-pane-icon">
              <FaCalendarDay/>
            </h3>
          </div>

          <div className="stat-pane-stats">
            <p className="stat-pane-title text-secondary">
              Best Day
            </p>

            <h3 className="stat-pane-main-stat">
              {userBestDayDate && formatDate(userBestDayDate, 'UTC', false)}
            </h3>

            <p className="stat-pane-title text-secondary">
              {userBestDayScore} points
            </p>
          </div>
        </div>

        <div className="screen-stat-pane">
          <div className="stat-pane-icon-wrapper">
            <h3 className="section-pane-icon">
              <TbMathAvg/>
            </h3>
          </div>

          <div className="stat-pane-stats">
            <p className="stat-pane-title text-secondary">
              Average Score
            </p>

            <h3 className="stat-pane-main-stat">
              {userAvgScore?.toFixed(0)}
            </h3>

            <p className="stat-pane-title text-secondary">
              {Math.sqrt(userVarScore)?.toFixed(0)} std dev
            </p>
          </div>
        </div>

        <div className="screen-stat-pane">
          <div className="stat-pane-icon-wrapper">
            <h3 className="section-pane-icon">
              <FaCalendarDays/>
            </h3>
          </div>

          <div className="stat-pane-stats">
            <p className="stat-pane-title text-secondary">
              Days Active
            </p>

            <h3 className="stat-pane-main-stat">
              {userDaysActive}
            </h3>
          </div>
        </div>
      </motion.div>
    </>
  )
};

export default UserStatistics;
